import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Ticket, Clock, Navigation, CheckCircle2, Trash2, LogIn, Tag, MapPinned, PiggyBank, Store, Globe, Copy } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { symbolFor } from "../utils/currency";
import { getRemaining, remainingLabel } from "../utils/countdown";
import { useLang } from "../context/LanguageContext";
import { thumb } from "../utils/image";
import { PriceCheckInline } from "../components/dashboard/PriceVerified";

const directionsUrl = (it) => `https://www.google.com/maps/dir/?api=1&destination=${it.lat},${it.lng}`;

const GrabCard = ({ it, onUndo, copyCode }) => {
  const { t } = useLang();
  const redeemed = it.status === "redeemed";
  const rem = getRemaining(it.expiresAt);
  const expired = rem.expired || it.dealGone;
  return (
    <div className={`bg-card border rounded-2xl p-4 flex gap-4 ${redeemed ? "border-[#16a34a]/40 bg-[#e7f6ec]/40 dark:bg-[#16341f]/40" : expired ? "border-border opacity-70" : "border-[#16a34a]"}`} data-testid={`my-deal-${it.dealId}`}>
      {it.kind === "code" ? (
        <div className="bg-white rounded-lg p-1.5 shrink-0 self-start"><QRCodeSVG value={it.code} size={64} /></div>
      ) : it.isOnline ? (
        <div className="h-[76px] w-[76px] rounded-lg bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center shrink-0"><Globe size={24} className="text-[#16a34a]" /></div>
      ) : it.image ? (
        <img src={thumb(it.image, 160)} alt="" loading="lazy" decoding="async" className="h-[76px] w-[76px] rounded-lg object-cover shrink-0" />
      ) : (
        <div className="h-[76px] w-[76px] rounded-lg bg-secondary flex items-center justify-center shrink-0"><Tag size={22} className="text-[#16a34a]" /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/deal/${it.dealId}`} className="font-bold text-foreground truncate block hover:text-[#16a34a] transition-colors">{it.dealTitle}</Link>
            <p className="text-xs text-muted-foreground truncate">{it.business}{it.location ? ` · ${it.location}` : ""}</p>
          </div>
          <span className="shrink-0 text-xs font-bold bg-[#16a34a] text-white px-2.5 py-1 rounded-full">{t("common.percentOff", { n: it.discount })}</span>
        </div>
        {it.kind === "code" && <p className="text-lg font-extrabold tracking-[0.25em] text-foreground mt-1" data-testid={`my-deal-code-${it.dealId}`}>{it.code}</p>}
        {it.isOnline && it.promoCode && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="text-base font-extrabold tracking-[0.2em] text-foreground bg-secondary rounded-lg px-3 py-1" data-testid={`my-deal-promo-${it.dealId}`}>{it.promoCode}</span>
            <button onClick={() => copyCode(it.promoCode)} data-testid={`my-deal-copy-promo-${it.dealId}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#16a34a] hover:underline"><Copy size={12} /> {t("mydeals.copy")}</button>
          </div>
        )}
        {redeemed ? (
          <>
            <p className="text-xs font-semibold text-[#16a34a] mt-1 flex items-center gap-1" data-testid={`my-deal-redeemed-${it.dealId}`}>
              <CheckCircle2 size={12} /> {t("mydeals.redeemed")}{it.redeemedAt ? ` · ${t("mydeals.redeemedOn", { date: String(it.redeemedAt).slice(0, 10) })}` : ""}{it.save ? ` · ${t("mydeals.youSavedAmount", { amount: `${symbolFor(it.currency)}${it.save}` })}` : ""}
            </p>
            {it.price != null && !it.dealGone && (
              <PriceCheckInline dealId={it.dealId} initial={it.myPriceCheck ?? null} />
            )}
          </>
        ) : expired ? (
          <p className="text-xs font-semibold text-muted-foreground mt-1">{t("mydeals.dealEnded")}</p>
        ) : (
          <p className={`text-xs font-semibold mt-1 flex items-center gap-1 ${rem.label && !rem.label.includes("d ") ? "text-[#dc2626]" : "text-muted-foreground"}`} data-testid={`my-deal-countdown-${it.dealId}`}>
            <Clock size={12} /> {rem.label ? t("mydeals.toRedeem", { time: remainingLabel(rem, t) }) : t("mydeals.noExpiry")}
          </p>
        )}
        {!redeemed && !expired && (
          <div className="flex items-center gap-4 mt-2.5 flex-wrap">
            <span className="text-xs font-semibold text-[#16a34a]">{it.kind === "code" ? t("mydeals.showAtCheckout") : it.isOnline ? t("mydeals.useOnlineCode") : t("mydeals.showAtBusiness", { business: it.business })}</span>
            {it.lat != null && (
              <a href={directionsUrl(it)} target="_blank" rel="noopener noreferrer" data-testid={`my-deal-directions-${it.dealId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563eb] hover:underline"><Navigation size={12} /> Directions</a>
            )}
            <button onClick={() => onUndo(it)} data-testid={`my-deal-undo-${it.dealId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-rose-500 transition-colors"><Trash2 size={12} /> {t("md.undoGrab")}</button>
          </div>
        )}
      </div>
    </div>
  );
};

const MyDeals = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const isShopper = user?.role === "shopper";

  useEffect(() => {
    if (!isShopper) { setLoading(false); return; }
    api.get("/my/grabs").then((r) => { setItems(r.data.items || []); setSaved(r.data.saved || {}); }).catch(() => {}).finally(() => setLoading(false));
  }, [isShopper]);

  const copyCode = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: t("mydeals.codeCopied"), description: t("mydeals.pasteAt", { code: value }) });
    } catch {
      toast({ title: t("mydeals.yourCode"), description: value });
    }
  };

  const undo = async (it) => {
    try {
      if (it.kind === "code") await api.delete(`/deals/${it.dealId}/code`); else await api.post(`/deals/${it.dealId}/unclaim`);
      setItems((l) => l.filter((x) => !(x.dealId === it.dealId && x.kind === it.kind)));
      toast({ title: t("mydeals.undone"), description: t("mydeals.undoneMsg", { title: it.dealTitle }) });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("mydeals.undoFailed"), description: typeof d === "string" ? d : t("common.tryAgain") });
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto text-center py-16 lp-fade-up" data-testid="my-deals-signed-out">
        <span className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Ticket size={30} className="text-[#16a34a]" /></span>
        <h1 className="text-2xl font-extrabold text-foreground">{t("mydeals.guestTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("mydeals.guestMsg")}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Link to="/login" data-testid="my-deals-login-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-bold px-6 py-3 rounded-xl transition-colors"><LogIn size={16} /> {t("auth.login")}</Link>
          <Link to="/signup" data-testid="my-deals-signup-btn" className="inline-flex items-center gap-2 bg-card border border-border hover:border-[#16a34a] text-foreground font-bold px-6 py-3 rounded-xl transition-colors">{t("mydeals.createAccount")}</Link>
        </div>
      </div>
    );
  }

  if (!isShopper) {
    return (
      <div className="max-w-xl mx-auto text-center py-16" data-testid="my-deals-business">
        <Store size={30} className="mx-auto text-[#16a34a] mb-3" />
        <h1 className="text-2xl font-extrabold text-foreground">{t("mydeals.businessTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("mydeals.businessMsg")}</p>
        {/* A shop manages its deals on My Account, not the shopper home page. */}
        <Link to="/account" data-testid="my-deals-business-cta" className="inline-block mt-5 bg-[#16a34a] text-white font-bold px-6 py-3 rounded-xl">{t("mydeals.goDashboard")}</Link>
      </div>
    );
  }

  const active = items.filter((it) => it.status !== "redeemed" && !getRemaining(it.expiresAt).expired && !it.dealGone);
  const ended = items.filter((it) => it.status !== "redeemed" && (getRemaining(it.expiresAt).expired || it.dealGone));
  const redeemed = items.filter((it) => it.status === "redeemed");
  const savedTxt = Object.entries(saved).map(([c, v]) => `${symbolFor(c)}${v.toFixed(2)}`).join(" + ");

  return (
    <div className="space-y-6 lp-fade-up" data-testid="my-deals-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2"><Ticket className="text-[#16a34a]" /> {t("mydeals.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("mydeals.subtitle")}</p>
        </div>
        {savedTxt && (
          <div className="inline-flex items-center gap-2 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] border border-[#16a34a]/30 px-4 py-2" data-testid="my-deals-saved-total">
            <PiggyBank size={18} className="text-[#16a34a]" />
            <span className="text-sm font-extrabold text-foreground">{t("mydeals.youSavedTotal", { amount: savedTxt })}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-secondary animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-10 text-center" data-testid="my-deals-empty">
          <span className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Ticket size={26} className="text-[#16a34a]" /></span>
          <h2 className="text-lg font-extrabold text-foreground">{t("mydeals.emptyTitle")}</h2>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">{t("mydeals.emptyMsg")}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            <Link to="/browse" data-testid="my-deals-browse-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-bold px-5 py-2.5 rounded-xl transition-colors"><Tag size={16} /> {t("nearexp.browseAll")}</Link>
            <Link to="/lytemap" data-testid="my-deals-map-btn" className="inline-flex items-center gap-2 bg-card border border-border hover:border-[#16a34a] text-foreground font-bold px-5 py-2.5 rounded-xl transition-colors"><MapPinned size={16} /> {t("browse.nearYou")}</Link>
          </div>
        </div>
      ) : (
        <>
          <section data-testid="my-deals-active">
            <h2 className="text-base md:text-lg font-extrabold text-foreground mb-3">{t("mydeals.ready")} <span className="text-muted-foreground font-semibold">({active.length})</span></h2>
            {active.length === 0 ? <p className="text-sm text-muted-foreground">{t("mydeals.nothingWaiting")} <Link to="/browse" className="text-[#16a34a] font-semibold hover:underline">{t("mydeals.grabNew")}</Link>.</p>
              : <div className="space-y-3">{active.map((it) => <GrabCard key={`${it.kind}-${it.dealId}`} it={it} onUndo={undo} copyCode={copyCode} />)}</div>}
          </section>
          {redeemed.length > 0 && (
            <section data-testid="my-deals-redeemed">
              <h2 className="text-base md:text-lg font-extrabold text-foreground mb-3">{t("mydeals.redeemed")} <span className="text-muted-foreground font-semibold">({redeemed.length})</span></h2>
              <div className="space-y-3">{redeemed.map((it) => <GrabCard key={`${it.kind}-${it.dealId}`} it={it} onUndo={undo} copyCode={copyCode} />)}</div>
            </section>
          )}
          {ended.length > 0 && (
            <section data-testid="my-deals-ended">
              <h2 className="text-base md:text-lg font-extrabold text-muted-foreground mb-3">{t("mydeals.ended")} <span className="font-semibold">({ended.length})</span></h2>
              <div className="space-y-3">{ended.map((it) => <GrabCard key={`${it.kind}-${it.dealId}`} it={it} onUndo={undo} copyCode={copyCode} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default MyDeals;
